import { ExpandableInfo } from '@/components/encounter/review/expandable-info';
import { useTranslation } from '@/contexts/i18n-context';
import { formatDate } from '@/util/formats';
import { ProviderInfo } from '@/api/api.schemas';
import styles from './message-from-provider-section.module.css';
import { ReactNode } from 'react';
import { DownloadTreatmentPlanButton } from './download-treatment-plan-button';
import { DownloadReferralLetterButton } from './download-referral-letter';
import { ProviderImage } from '../encounter/provider/provider-image';

interface BaseMessageFromProviderSectionProps {
  messageFromProvider: string | null | undefined;
  assignedProvider: ProviderInfo;
  approvedProvider: ProviderInfo | null | undefined;
}

interface MessageFromProviderSectionProps extends BaseMessageFromProviderSectionProps {
  genericContent: ReactNode;
  children?: ReactNode;
}

const translationBase = 'visit.treatmentPlan.providerMessages';

function MessageFromProviderSection({
  messageFromProvider,
  assignedProvider,
  approvedProvider,
  genericContent,
  children,
}: MessageFromProviderSectionProps) {
  const { t } = useTranslation();

  return (
    <ExpandableInfo title={t(`${translationBase}.title`)} isOpen>
      <div className={styles.opinionContainer}>
        {genericContent}

        <div className={styles.providersContainer}>
          <div className={styles.providerInfoContainer}>
            <ProviderImage url={assignedProvider.img} className={styles.providerImg} />
            <p>
              {t(`${translationBase}.treatedBy`, {
                providerName: assignedProvider.name,
              })}
            </p>
          </div>
          {approvedProvider && (
            <div className={styles.providerInfoContainer}>
              <ProviderImage url={approvedProvider.img} className={styles.providerImg} />
              <p>
                {t(`${translationBase}.approvedBy`, {
                  providerName: approvedProvider.name,
                })}
              </p>
            </div>
          )}
        </div>
      </div>

      {messageFromProvider && (
        <p className={styles.messageFromProviderContent}>{messageFromProvider}</p>
      )}

      {children}
    </ExpandableInfo>
  );
}

interface CompleteMessageFromProviderSectionProps extends BaseMessageFromProviderSectionProps {
  encounterId: number;
  followUpVisitDate: Date | null | undefined;
  referralTextHtml: string | null | undefined;
}

export function CompleteMessageFromProviderSection(props: CompleteMessageFromProviderSectionProps) {
  const { encounterId, followUpVisitDate, referralTextHtml, ...rest } = props;
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.messageFromProviderDivider} />
      <MessageFromProviderSection
        {...rest}
        genericContent={<p className="italic">{t(`${translationBase}.opinion`)}</p>}
      >
        {(followUpVisitDate || referralTextHtml) && (
          <div className={styles.recommendationsSection}>
            {followUpVisitDate && (
              <p>
                {t(`${translationBase}.followUpVisit`, {
                  date: formatDate(followUpVisitDate, { format: 'user' }),
                })}
              </p>
            )}

            {referralTextHtml && (
              <>
                <div dangerouslySetInnerHTML={{ __html: referralTextHtml }} />
                <DownloadReferralLetterButton
                  encounterId={encounterId}
                  fileName="referral-letter.pdf"
                  className={styles.button}
                >
                  {t(`${translationBase}.referralLetter`)}
                </DownloadReferralLetterButton>
              </>
            )}
          </div>
        )}
      </MessageFromProviderSection>
    </>
  );
}

interface TriageOutMessageFromProviderSectionProps extends BaseMessageFromProviderSectionProps {
  triageOutMessage: string | null | undefined;
  encounterId: number;
  hasReferralSpecialist: boolean;
}

export function TriageOutMessageFromProviderSection(
  props: TriageOutMessageFromProviderSectionProps,
) {
  const { triageOutMessage, encounterId, hasReferralSpecialist, ...rest } = props;
  const { t } = useTranslation();

  return (
    <MessageFromProviderSection
      {...rest}
      genericContent={
        <>
          {triageOutMessage && (
            <div className="italic" dangerouslySetInnerHTML={{ __html: triageOutMessage }} />
          )}
          <DownloadTreatmentPlanButton
            encounterId={encounterId}
            fileName="triaged-out-visit.pdf"
            className={styles.button}
          >
            {t(`${translationBase}.savePdfTriageOut`)}
          </DownloadTreatmentPlanButton>
          {hasReferralSpecialist && (
            <DownloadReferralLetterButton
              encounterId={encounterId}
              fileName="referral-letter.pdf"
              className={styles.button}
            >
              {t(`${translationBase}.referralLetter`)}
            </DownloadReferralLetterButton>
          )}
        </>
      }
    />
  );
}
